import "@/App.css";
import "./styles/dark-theme-overrides.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CartProvider } from "./contexts/CartContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import POS from "./pages/POS";
import Products from "./pages/Products";
import Inventory from "./pages/Inventory";
import Suppliers from "./pages/Suppliers";
import Sales from "./pages/Sales";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import ScanProduct from "./pages/ScanProduct";
import Help from "./pages/Help";
import Pricing from "./pages/Pricing";
import LandingPage from "./pages/LandingPage";
import TimeClock from "./pages/TimeClock";
import AdminPanel from "./pages/AdminPanel";
import KDS from "./pages/KDS";
import StoreSettings from "./pages/StoreSettings";
import InvoiceUpload from "./pages/InvoiceUpload";
import InvoiceCapture from "./pages/InvoiceCapture";
import InvoiceReview from "./pages/InvoiceReview";
import InventoryAlerts from "./pages/InventoryAlerts";
import ReorderSuggestions from "./pages/ReorderSuggestions";
import ComplianceDashboard from "./pages/ComplianceDashboard";
import LabelGenerator from "./pages/LabelGenerator";
import SetupWizard from "./pages/SetupWizard";
import DailyCloseout from "./pages/DailyCloseout";
import CloseoutHistory from "./pages/CloseoutHistory";
import DeliveryHub from "./pages/DeliveryHub";
import HardwareSetup from "./pages/HardwareSetup";
import Employees from "./pages/Employees";
import InventoryImport from "./pages/InventoryImport";

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
    const { isAuthenticated, user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user?.role)) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}

function AppRoutes() {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <Routes>
            <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
            
            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <Dashboard />
                </ProtectedRoute>
            } />
            
            <Route path="/pos" element={
                <ProtectedRoute>
                    <POS />
                </ProtectedRoute>
            } />
            
            <Route path="/products" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <Products />
                </ProtectedRoute>
            } />
            
            <Route path="/inventory" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <Inventory />
                </ProtectedRoute>
            } />
            
            <Route path="/suppliers" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <Suppliers />
                </ProtectedRoute>
            } />
            
            <Route path="/sales" element={
                <ProtectedRoute>
                    <Sales />
                </ProtectedRoute>
            } />
            
            <Route path="/reports" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <Reports />
                </ProtectedRoute>
            } />
            
            <Route path="/timeclock" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <TimeClock />
                </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Settings />
                </ProtectedRoute>
            } />
            
            <Route path="/scan" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <ScanProduct />
                </ProtectedRoute>
            } />
            
            <Route path="/help" element={
                <ProtectedRoute>
                    <Help />
                </ProtectedRoute>
            } />
            
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/subscription/success" element={<Pricing />} />
            <Route path="/subscription/cancelled" element={<Pricing />} />

            <Route path="/admin" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <AdminPanel />
                </ProtectedRoute>
            } />
            
            <Route path="/kds" element={
                <ProtectedRoute>
                    <KDS />
                </ProtectedRoute>
            } />
            
            <Route path="/store-settings" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <StoreSettings />
                </ProtectedRoute>
            } />
            
            <Route path="/setup" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <SetupWizard />
                </ProtectedRoute>
            } />
            
            <Route path="/closeout" element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'cashier']}>
                    <DailyCloseout />
                </ProtectedRoute>
            } />
            
            <Route path="/closeout/history" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <CloseoutHistory />
                </ProtectedRoute>
            } />
            
            <Route path="/delivery" element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'cashier']}>
                    <DeliveryHub />
                </ProtectedRoute>
            } />
            
            <Route path="/hardware-setup" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <HardwareSetup />
                </ProtectedRoute>
            } />

            <Route path="/employees" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <Employees />
                </ProtectedRoute>
            } />

            <Route path="/inventory-import" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <InventoryImport />
                </ProtectedRoute>
            } />
            
            <Route path="/invoices/upload" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <InvoiceCapture />
                </ProtectedRoute>
            } />
            
            <Route path="/invoices/:id/review" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <InvoiceReview />
                </ProtectedRoute>
            } />
            
            <Route path="/inventory/alerts" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <InventoryAlerts />
                </ProtectedRoute>
            } />
            
            <Route path="/inventory/reorder" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <ReorderSuggestions />
                </ProtectedRoute>
            } />
            
            <Route path="/compliance" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <ComplianceDashboard />
                </ProtectedRoute>
            } />
            
            <Route path="/labels" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <LabelGenerator />
                </ProtectedRoute>
            } />
            
            {/* Landing page for non-authenticated users, dashboard for authenticated */}
            <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
            <Route path="*" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
        </Routes>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <CartProvider>
                    <BrowserRouter>
                        <AppRoutes />
                        <Toaster position="top-right" richColors />
                    </BrowserRouter>
                </CartProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
